import type { Track } from "../api.js";
import type { AutoMix, AutoMixSnapshot } from "../automix/controller.js";
import { duration as formatDuration, escapeHtml } from "./format.js";

export class AutoMixPanel {
  private snapshot: AutoMixSnapshot;

  constructor(
    private readonly root: HTMLElement,
    private readonly automix: AutoMix,
    private readonly selectedTrack: () => Track | null,
  ) {
    this.snapshot = automix.snapshot();
    this.root.classList.add("automix");
    this.root.innerHTML = this.chrome();
    this.bind();
    this.render();
  }

  private chrome(): string {
    return `
      <div class="am-row">
        <button id="am-arm" type="button">Start auto-mix</button>
        <button id="am-shake" type="button" title="Pick something different right now">Mix it up</button>
        <button id="am-skip" type="button" title="Blend to the next track from the coming bar (N)">Blend now</button>
        <button id="am-cut" type="button" title="Roll out and cut within a couple of bars (B)">Cut now</button>
        <button id="am-queue" type="button" title="Queue the selected library track">Queue selected</button>

      </div>

      <div class="am-settings">
        <label class="am-setting">
          <span class="muted">tempo drift</span>
          <input id="am-temperature" type="range" min="0" max="1" step="0.01" value="0.35" />
          <span id="am-temp-label" class="muted num">0.35</span>
        </label>
        <label class="am-setting" title="Longest a single track may hold the mix before a random excerpt is used instead">
          <span class="muted">max per track</span>
          <input id="am-segment" type="range" min="60" max="1500" step="30" value="420" />
          <span id="am-segment-label" class="muted num">7:00</span>
        </label>
        <label class="am-setting" title="Blend length range for transitions involving a track with no steady tempo">
          <span class="muted">ambient blend</span>
          <input id="am-ambient-min" type="range" min="4" max="60" step="1" value="12" />
          <input id="am-ambient-max" type="range" min="4" max="90" step="1" value="30" />
          <span id="am-ambient-label" class="muted num">12–30s</span>
        </label>
      </div>

      <div class="am-status">
        <div><span class="muted">now</span> <span id="am-now">—</span></div>
        <div><span class="muted">next</span> <span id="am-next">—</span></div>
        <div id="am-transition" class="muted"></div>
      </div>

      <div id="am-queued" class="am-queued"></div>
      <div id="am-message" class="am-message"></div>
      <details class="am-history">
        <summary class="muted">recent transitions</summary>
        <ul id="am-history-list"></ul>
      </details>
    `;
  }

  private $<T extends HTMLElement = HTMLElement>(id: string): T {
    const node = this.root.querySelector(`#${id}`);
    if (!node) throw new Error(`missing #${id}`);
    return node as T;
  }

  private bind(): void {
    this.$("am-arm").addEventListener("click", () => {
      this.automix.toggle();
      this.render();
    });
    this.$("am-shake").addEventListener("click", () => this.automix.mixItUp());
    this.$("am-skip").addEventListener("click", () => void this.automix.blendNow("smooth"));
    this.$("am-cut").addEventListener("click", () => void this.automix.blendNow("aggressive"));
    this.$("am-queue").addEventListener("click", () => {
      const track = this.selectedTrack();
      if (track) this.automix.enqueue(track.id);
    });

    const temperature = this.$<HTMLInputElement>("am-temperature");
    temperature.addEventListener("input", () => {
      this.automix.config.temperature = Number(temperature.value);
      this.$("am-temp-label").textContent = Number(temperature.value).toFixed(2);
    });

    const segment = this.$<HTMLInputElement>("am-segment");
    segment.addEventListener("input", () => {
      this.automix.config.maxSegmentSeconds = Number(segment.value);
      this.$("am-segment-label").textContent = formatDuration(Number(segment.value));
    });

    const ambientMin = this.$<HTMLInputElement>("am-ambient-min");
    const ambientMax = this.$<HTMLInputElement>("am-ambient-max");
    const applyAmbient = () => {
      // Keep the pair ordered rather than letting min exceed max, which would
      // silently produce a negative range.
      const low = Math.min(Number(ambientMin.value), Number(ambientMax.value));
      const high = Math.max(Number(ambientMin.value), Number(ambientMax.value));
      this.automix.config.ambientBlendMin = low;
      this.automix.config.ambientBlendMax = high;
      this.$("am-ambient-label").textContent = `${low}–${high}s`;
    };
    ambientMin.addEventListener("input", applyAmbient);
    ambientMax.addEventListener("input", applyAmbient);

    this.$("am-queued").addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>("[data-remove]");
      if (button) this.automix.dequeue(Number(button.dataset["remove"]));
    });
  }

  update(snapshot: AutoMixSnapshot): void {
    this.snapshot = snapshot;
    this.render();
  }

  /** Called from the shared animation loop, for the countdown only. */
  renderFrame(): void {
    if (!this.snapshot.armed) return;
    const live = this.automix.snapshot();
    this.snapshot = live;
    this.renderTransition(live);
  }

  private renderTransition(s: AutoMixSnapshot): void {
    const node = this.$("am-transition");
    if (!s.armed) {
      node.textContent = "";
      return;
    }
    if (s.countdown !== null && s.plannedMethod) {
      const labels: Record<string, string> = {
        "beat-aligned": "beat-aligned",
        "tempo-ramp": "tempo bend",
        "loop-roll": "roll & cut",
        crossfade: "crossfade",
      };
      const label = labels[s.plannedMethod] ?? s.plannedMethod;
      node.innerHTML =
        `<span class="working">${label}</span> in ${formatDuration(s.countdown)}` +
        ` <span class="muted">— ${escapeHtml(s.plannedReason ?? "")}</span>`;
      return;
    }
    node.innerHTML = `<span class="muted">${escapeHtml(this.stateLabel(s))}</span>`;
  }

  private stateLabel(s: AutoMixSnapshot): string {
    switch (s.nextState) {
      case "selecting":
        return "choosing the next track…";
      case "loading":
        return "loading the next track…";
      case "ready":
        return "next track ready, waiting to transition";
      case "failed":
        return "could not load a next track — will retry";
      default:
        return "playing";
    }
  }

  private render(): void {
    const s = this.snapshot;

    const arm = this.$<HTMLButtonElement>("am-arm");
    arm.textContent = s.armed ? "Stop auto-mix" : "Start auto-mix";
    arm.classList.toggle("recording", s.armed);
    for (const id of ["am-shake", "am-skip", "am-cut"]) {
      this.$<HTMLButtonElement>(id).disabled = !s.armed;
    }

    const label = (track: Track | null) =>
      track
        ? `${track.artist ? `${escapeHtml(track.artist)} — ` : ""}${escapeHtml(track.title ?? track.path)}`
        : "—";

    this.$("am-now").innerHTML = label(s.nowPlaying);
    this.$("am-next").innerHTML = label(s.next);
    this.renderTransition(s);

    const queued = this.$("am-queued");
    if (s.queued.length === 0) {
      queued.innerHTML = "";
    } else {
      queued.innerHTML =
        `<span class="muted">queued:</span> ` +
        s.queued
          .map(
            (id) =>
              `<button class="chip" type="button" data-remove="${id}" title="Remove from queue">#${id} ✕</button>`,
          )
          .join(" ");
    }

    // The pool is shown whenever it is filtered or too small, so "auto-mix will
    // not start" and "this tag only has one analysed track" are never two
    // separate discoveries.
    const poolNote =
      s.poolTag !== null
        ? `<span class="muted">Pool: ${s.poolSize} from “${escapeHtml(s.poolTag)}”</span>`
        : s.poolSize < 2
          ? `<span class="warn">Pool: ${s.poolSize}</span>`
          : "";

    this.$("am-message").innerHTML = [
      poolNote,
      s.message ? `<span class="muted">${escapeHtml(s.message)}</span>` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    this.$("am-history-list").innerHTML = s.history
      .map(
        (entry) =>
          `<li><b>${escapeHtml(entry.title)}</b> <span class="muted">${escapeHtml(entry.method)} — ${escapeHtml(entry.reason)}</span></li>`,
      )
      .join("");

    if (!s.usingWorker && s.armed) {
      this.$("am-message").innerHTML +=
        ` <span class="warn">Timing worker unavailable; transitions may drift if this tab is backgrounded.</span>`;
    }
  }
}
